'use client';

import { Column, Container } from '@aurora-components';
import { type FragmentType, getFragmentData } from '@gql';
import { Card } from 'components/aurora/card/Card';
import { ContentPostFragment, ContentPostsListFragment, ImageFragment } from 'lib/shared/content-posts-gql';
import { useParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { convertDate } from 'utils/convertDate';
import { getImage } from 'utils/get-image';
import {
  CardSectionPersonsFragment,
  CardSectionSimpleBoxesFragment,
  VildarCardSectionCardsFragment,
} from './card-section-gql';
import { ContentPostContainer, PaginationContainer, PaginationItem } from './card-section.styles';

const fetcher = (url: string) => fetch(url).then((r) => r.json());

const SimpleBoxCards = (props: FragmentType<typeof CardSectionSimpleBoxesFragment>) => {
  const { simple_boxes } = getFragmentData(CardSectionSimpleBoxesFragment, props);

  return (
    <ContentPostContainer>
      {simple_boxes?.map((item, index) => {
        return (
          <Card
            card_template={'base_card'}
            key={index}
            card={{
              text: item?.box?.text || '',
              display_title: item?.box?.headline || '',
              backgroundColor: item?.box?.background_color || '',
            }}
          />
        );
      })}
    </ContentPostContainer>
  );
};

const PersonCards = (props: FragmentType<typeof CardSectionPersonsFragment>) => {
  const { persons } = getFragmentData(CardSectionPersonsFragment, props);

  return (
    <ContentPostContainer>
      {persons?.map((item, index) => {
        return (
          <Card
            card_template={'simple_presentation_card'}
            key={index}
            card={{
              text: item?.person?.description || '',
              display_title: item?.person?.name || '',
            }}
          />
        );
      })}
    </ContentPostContainer>
  );
};

const Pagination = ({
  onSelectItem: callbackSelect,
  limit,
  total,
}: {
  onSelectItem: (value: number) => void;
  limit: number;
  total: number;
}) => {
  const [selected, setSelected] = useState(0);

  const SelectPage = (value: number) => {
    setSelected(value);
    callbackSelect(value * limit);
  };

  const items = useMemo(() => {
    const paginationItems = [];

    const pages = Math.ceil(total / limit);
    for (let i = 0; i < pages; i++) {
      paginationItems.push(
        <PaginationItem key={i} onClick={() => SelectPage(i)} $selected={selected === i}>
          {i + 1}
        </PaginationItem>
      );
    }
    return paginationItems;
  }, [selected]);

  return <PaginationContainer>{items.length > 0 ? items : <></>}</PaginationContainer>;
};
const ContentPosts = ({
  type,
  postLimit,
  pagination,
}: {
  postLimit: number | null | undefined;
  type: string | null | undefined;
  pagination: boolean;
}) => {
  const { locale, slug } = useParams() as { locale: string; slug: string };
  const [totalPosts, setTotalPosts] = useState<number>(0);

  const [skip, setSkip] = useState<number>(0);

  const { data } = useSWR(`/api/posts/?type=${type}&limit=${postLimit}&locale=${locale}&skip=${skip}`, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const posts = getFragmentData(ContentPostsListFragment, data);

  useEffect(() => {
    if (data?.total && data?.total !== totalPosts) {
      setTotalPosts(data?.total || 0);
    }
  }, [data]);

  const onPaginate = (value: number) => {
    setSkip(value);
  };

  const items = useMemo(
    () =>
      posts?.items?.sort((a, b) => {
        const postA = getFragmentData(ContentPostFragment, a);
        const postB = getFragmentData(ContentPostFragment, b);
        if (!postA?.date || !postB?.date) return 1;

        return new Date(postB.date).getTime() - new Date(postA.date).getTime();
      }) || [],
    [posts]
  );

  return (
    <Container $maxWidth $fullHeight>
      <Column $horizontalAlign="center" $span={{ xs: 12, md: 12 }}>
        <ContentPostContainer>
          {items.length > 0 &&
            items.map((item, index) => {
              const post = getFragmentData(ContentPostFragment, item);
              const image = getFragmentData(ImageFragment, post);

              const { gravity, crop_resize: cropResize, alt_text: altText, assetConnection } = image?.image?.[0] || {};

              const imageData = getImage(assetConnection);
              const postDate = convertDate(locale, post?.date);

              return (
                <Card
                  key={index}
                  card_template="content_post"
                  card={{
                    display_title: post?.display_title || '',
                    text: post?.short_description || '',
                    linkLabel: post?.link_label || '',
                    link: `${locale === 'en' ? '/en/' : ''}${slug?.replace('_', '-')}${post?.url}` || '',
                    date: postDate,
                    showDate: post?.show_date,
                    image: {
                      gravity: gravity || '',
                      crop_resize: cropResize || '',
                      alt_text: altText || '',
                      imageId: imageData?.id || '',
                      dimensions: { height: imageData?.dimension?.height, width: imageData?.dimension?.width },
                      assetConnection: {
                        edges: [
                          {
                            node: {
                              content_type: imageData?.suffix || '',
                            },
                          },
                        ],
                      },
                    },
                  }}
                />
              );
            })}
        </ContentPostContainer>
      </Column>
      <Column $horizontalAlign="end" $span={{ md: 4 }}>
        {pagination && totalPosts && postLimit ? (
          <Pagination onSelectItem={onPaginate} limit={postLimit} total={totalPosts} />
        ) : (
          <></>
        )}
      </Column>
    </Container>
  );
};

export default function Cards({
  cardTemplate,
  postLimit,
  type,
  pagination = false,
  ...props
}: {
  cardTemplate: string | null | undefined;
  postLimit: number | null | undefined;
  type: string | null | undefined;
  pagination: boolean;
}) {
  const section = getFragmentData(VildarCardSectionCardsFragment, props);

  switch (cardTemplate) {
    case 'color_boxes':
      return <SimpleBoxCards {...section} />;
    case 'person':
      return <PersonCards {...section} />;
    case 'content_post':
      return <ContentPosts type={type} postLimit={postLimit} pagination={pagination} />;
  }
}
